import { applyLead, applyPlay } from '../game/Chain.js';
import { enumeratePlays } from '../game/MoveValidator.js';
import { playScore } from '../game/scoring/fivesScoring.js';
import { type Chain, type Tile } from '../game/types.js';
import type { DominoRuleSet } from '../game/variants/types.js';

/**
 * AI-facing wrapper over the engine's `enumeratePlays`. Computes each
 * candidate's *post-play* open-end sum and immediate score so the evaluator
 * doesn't pay the cost of re-applying every move it scores.
 *
 * Kept deliberately thin — variant rules live in `game/`; this layer only
 * adds scoring + lookahead metadata.
 */
export interface PlayMove {
  readonly tile: Tile;
  readonly armIndex: number;
  /** Sum of open-end values after this play (active arms only). */
  readonly openEndsAfter: number;
  /** Immediate score (open-ends sum if a multiple of scoreMultiple, else 0). */
  readonly score: number;
  /** Did this play complete the spinner (lead-double or mid-chain first double). */
  readonly spinnerFormed: boolean;
  /** The chain state after this play — pre-computed for lookahead callers. */
  readonly chainAfter: Chain;
}

export class MoveEnumerator {
  constructor(private readonly ruleSet: DominoRuleSet) {}

  /**
   * Enumerate every legal play for `hand` against `chain`. Pre-lead (empty
   * chain), every tile is returned with `armIndex = -1` (caller passes -1 to
   * `applyLead`). Each result carries the post-play open-ends sum and
   * immediate score.
   */
  enumerate(
    chain: Chain,
    hand: readonly Tile[],
    forcedLeadTile: Tile | null = null,
  ): PlayMove[] {
    const candidates = enumeratePlays(chain, hand, forcedLeadTile);
    const out: PlayMove[] = [];
    const isLead = chain.lead === null;
    for (const { tile, armIndex } of candidates) {
      const result = isLead
        ? applyLead(chain, tile)
        : applyPlay(chain, armIndex, tile, this.ruleSet.spinner);
      const rawScore = playScore(this.ruleSet, result.openEndsSum);
      // Телефон: lead only scores when it's [5|5]. The AI must see the same
      // payoff the engine will award, otherwise it'll happily lead with
      // tiles thinking they score 5/10/15 when they actually score 0.
      const score =
        isLead && this.ruleSet.leadScoresOnlyOnDoubleFive && !(tile[0] === 5 && tile[1] === 5)
          ? 0
          : rawScore;
      out.push({
        tile,
        armIndex,
        openEndsAfter: result.openEndsSum,
        score,
        spinnerFormed: result.spinnerFormed,
        chainAfter: result.chain,
      });
    }
    return out;
  }
}
